// src/utils/ga.ts

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

export type GA4EventParams = {
  // standard GA4 parameter shapes plus custom keys
  // keep it flexible: values can be string/number/boolean/object/array
  [key: string]: string | number | boolean | object | Array<any> | undefined;
};

/**
 * Send an event to GA4 via gtag.
 * Returns true if the event was dispatched to window.gtag, false otherwise.
 *
 * @param eventName - GA4 event name (e.g. 'purchase', 'select_content', 'button_click')
 * @param params - optional event parameters (e.g. { currency: 'USD', value: 9.99, item_id: 'sku-123' })
 */
export const trackGAEvent = (
  eventName: string,
  params: GA4EventParams = {},
): boolean => {
  // server-side guard
  if (typeof window === "undefined") return false;

  // clone and strip undefined values (gtag doesn't like undefined fields)
  const cleanParams: Record<string, any> = {};
  Object.keys(params).forEach((key) => {
    const val = params[key];
    if (typeof val !== "undefined") {
      cleanParams[key] = val;
    }
  });

  if (typeof window.gtag !== "function") {
    if (process.env.NODE_ENV !== "production") {
      console.log("📊 [GA4 Event (Simulation)]:", eventName, cleanParams);
    }
    return false;
  }

  try {
    // GA4: gtag('event', <event_name>, { ...params })
    window.gtag("event", eventName, cleanParams);
    if (process.env.NODE_ENV !== "production") {
      console.log("📊 [GA4 Event (Sent)]:", eventName, cleanParams);
    }
    return true;
  } catch (err) {
    // swallow to avoid crashing the app; optionally log in dev
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error("gtag error sending event:", err);
    }
    return false;
  }
};

export default trackGAEvent;
